/**
 * Stale-while-revalidate for remote (Nostr) tree bundles: paint IndexedDB
 * cache immediately, then quietly replace when the network returns newer JSON.
 */

import { parseNostrTreeUrl, formatNostrTreeUrl } from '../../nostr/api/nostr-refs.js';
import { ensureConnectedNostr } from '../../../shared/lib/connected-services/index.js';
import { shouldShowMobileUI } from '../../../shared/ui/breakpoints.js';
import { parseArboritoTreeBundle } from '../../forest/api/arborito-tree-bundle.js';
import { importComposedTreeFromBundle } from '../../forest/api/import-composed-tree-bundle.js';
import { DataProcessor } from '../../tree-graph/api/data-processor.js';
import { normalizeLoadedTreeJson } from '../../tree-graph/api/tree-load-pipeline.js';
import { sanitizeImportedTreeJson } from '../../tree-graph/api/tree-import-sanitize.js';
import { putTreeBundleCache } from './tree-bundle-cache.js';
import { scheduleAutoWebTorrentSeeder } from '../../p2p-webtorrent/api/auto-webtorrent-seeder.js';
import { isUniverseRevokedError, promptStudentUniverseRevoked, clearUniverseRevokeStudentState, hasPendingUniverseRevokePrompt } from './universe-revoked.js';

/** Rough stamp so identical network payloads skip a full graph remount. */
export function treeBundleRoughStamp(json) {
    if (!json || typeof json !== 'object') return '';
    const m = json.meta && typeof json.meta === 'object' ? json.meta : {};
    const nodes = Array.isArray(json.nodes) ? json.nodes.length : 0;
    const branches = Array.isArray(json.branches) ? json.branches.length : 0;
    /* Prefer updatedAt; fall back to export/create times so republishes that only
     * change node icons (or lesson counts) still remount. Flat nodes/branches
     * are usually 0 for arborito-bundle trees (structure lives under tree.languages). */
    return [
        String(m.updatedAt || m.exportedAt || m.createdAt || ''),
        String(m.publishedAt || ''),
        String(m.shareCode || ''),
        String(m.nostrBundleFormat || ''),
        String(m.nostrLessonChunksCount ?? ''),
        String(m.icon || ''),
        String(m.gen || ''),
        m.skeleton === true ? 'skel' : 'full',
        String(json.format || ''),
        String(nodes),
        String(branches),
    ].join('|');
}

function nostrUrlKey(url) {
    const g = parseNostrTreeUrl(url);
    return g ? formatNostrTreeUrl(g.pub, g.universeId) : String(url || '').trim();
}

/**
 * After a composed playlist mounts, `activeSource.id` becomes the local treeId while
 * SWR still tracks the community/nostr source id — treat matching publishedNetworkUrl
 * as the same mount target.
 */
function isRemoteSwrStillActive(store, source, sourceId) {
    const active = store?.state?.activeSource;
    if (!active) return false;
    if (String(active.id || '') === String(sourceId || '')) return true;
    if (active.type === 'composed-tree' || active.treeId) {
        const treeId = String(active.treeId || active.id || '').trim();
        const entry = treeId ? store.userStore?.getTree?.(treeId) : null;
        const pub = nostrUrlKey(entry?.publishedNetworkUrl || '');
        const src = nostrUrlKey(source?.url || '');
        if (pub && src && pub === src) return true;
    }
    return false;
}

/** @type {Map<string, Promise<void>>} */
const remoteSwrInFlight = new Map();

/**
 * @param {import('../../../core/store.js').Store} store
 * @param {object} source
 * @param {{ epoch: number, connectPromise?: Promise<unknown>|null }} opts
 */
export async function refreshRemoteTreeBundleInBackground(store, source, opts) {
    const epoch = Number(opts?.epoch) || 0;
    const sourceId = String(source?.id || '');
    if (!sourceId || !store) return;

    /* Coalesce duplicate SWR for the same mount epoch — never block a newer mount. */
    const flightKey = `${sourceId}@${epoch}`;
    const existing = remoteSwrInFlight.get(flightKey);
    if (existing) {
        await existing;
        return;
    }

    const run = (async () => {
        try {
            await refreshRemoteTreeBundleInBackgroundBody(store, source, opts, epoch, sourceId);
        } finally {
            if (remoteSwrInFlight.get(flightKey) === run) {
                remoteSwrInFlight.delete(flightKey);
            }
        }
    })();
    remoteSwrInFlight.set(flightKey, run);
    await run;
}

/**
 * @param {import('../../../core/store.js').Store} store
 * @param {object} source
 * @param {{ epoch: number, connectPromise?: Promise<unknown>|null }} opts
 * @param {number} epoch
 * @param {string} sourceId
 */
async function refreshRemoteTreeBundleInBackgroundBody(store, source, opts, epoch, sourceId) {
    try {
        if (opts?.connectPromise) {
            await opts.connectPromise;
        } else {
            await ensureConnectedNostr(store, {
                timeoutMs: shouldShowMobileUI() ? 20000 : 12000,
            });
        }
        if (epoch !== store._curriculumMountEpoch) return;
        if (!isRemoteSwrStillActive(store, source, sourceId)) return;

        const out = await store.sourceManager.loadData(
            source,
            store.state.lang,
            true,
            null
        );
        if (epoch !== store._curriculumMountEpoch) return;
        if (!isRemoteSwrStillActive(store, source, sourceId)) return;
        if (!out?.json) return;

        try {
            clearUniverseRevokeStudentState(out.finalSource || source);
        } catch {
            /* ignore */
        }

        if (
            (out.finalSource?.origin === 'nostr') &&
            parseNostrTreeUrl(String(out.finalSource?.url || source.url || ''))
        ) {
            store._treeForumHydratedForSourceId = null;
        }

        if (parseArboritoTreeBundle(out.json)) {
            const treeRef = parseNostrTreeUrl(
                String((out.finalSource && out.finalSource.url) || source.url || '')
            );
            const nostrKey = treeRef
                ? formatNostrTreeUrl(treeRef.pub, treeRef.universeId)
                : '';
            const prevEntry =
                (nostrKey
                    ? (store.userStore?.state?.trees || []).find(
                          (t) => String(t?.publishedNetworkUrl || '').trim() === nostrKey
                      )
                    : null) ||
                (store.state.activeSource?.type === 'composed-tree'
                    ? store.userStore?.getTree?.(store.state.activeSource.treeId)
                    : null);
            const prevGen = String(prevEntry?.publishedBundleGen || '').trim();
            const nextGen = String(out.json?.meta?.gen || '').trim();
            const nextStamp = treeBundleRoughStamp(out.json);

            const entry = await importComposedTreeFromBundle(store, out.json, {
                treeRef: treeRef || undefined,
                shareCode: (out.finalSource && out.finalSource.shareCode) || undefined,
            });
            if (epoch !== store._curriculumMountEpoch) return;
            if (!isRemoteSwrStillActive(store, source, sourceId)) return;
            const composedId = String(entry?.id || '').trim();
            if (!composedId) return;

            /* Offline freeze: keep the device snapshot; do not pull live curriculum. */
            if (store.userStore?.isTreeFrozen?.(composedId)) return;

            const alreadyComposed =
                !!store.state.data &&
                (String(store.state.activeSource?.treeId || store.state.activeSource?.id || '') ===
                    composedId ||
                    store.state.activeSource?.type === 'composed-tree');

            const sameGen = !!(nextGen && prevGen && prevGen === nextGen);
            const stampUnchanged =
                !!(store._lastComposedNetworkStamp &&
                    nextStamp &&
                    store._lastComposedNetworkStamp === nextStamp);
            if (alreadyComposed && (sameGen || (!nextGen && stampUnchanged))) {
                store._lastComposedNetworkStamp = nextStamp || store._lastComposedNetworkStamp;
                return;
            }

            store._lastComposedNetworkStamp = nextStamp || '';
            if (alreadyComposed) {
                /* Republish: force member refetch (bypass 1h IDB freshness). Quiet remount. */
                await store.loadComposedTree(composedId, true);
                return;
            }
            /* First paint as composed — soft open keeps a warm canvas. */
            await store.loadComposedTree(composedId, false);
            return;
        }

        let graphJson = normalizeLoadedTreeJson(out.json, store, out.finalSource);
        if (!graphJson) return;
        const { tree: sanitized } = sanitizeImportedTreeJson(graphJson);
        graphJson = sanitized;
        if (!graphJson) return;

        const prevStamp = treeBundleRoughStamp(store.state.rawGraphData);
        const nextStamp = treeBundleRoughStamp(graphJson);
        if (prevStamp && nextStamp && prevStamp === nextStamp) {
            if (out.finalSource?.id) {
                void putTreeBundleCache(String(out.finalSource.id), {
                    treeJson: graphJson,
                    url: out.finalSource.url,
                    origin: out.finalSource.origin,
                });
            }
            return;
        }

        if (typeof store.refreshTreeNetworkGovernance === 'function') {
            await store.refreshTreeNetworkGovernance(out.finalSource);
        }
        if (epoch !== store._curriculumMountEpoch) return;
        if (!isRemoteSwrStillActive(store, source, sourceId)) return;

        DataProcessor.process(store, graphJson, out.finalSource, {
            suppressReadmeAutoOpen: true,
            carryOverSelection: true,
        });
        if (out.finalSource?.id && store.state.rawGraphData) {
            void putTreeBundleCache(String(out.finalSource.id), {
                treeJson: store.state.rawGraphData,
                url: out.finalSource.url,
                origin: out.finalSource.origin,
            });
        }
        try {
            if (typeof store.syncNostrPresenceFromActiveSource === 'function') {
                store.syncNostrPresenceFromActiveSource(out.finalSource);
            }
        } catch {
            /* ignore */
        }
        scheduleAutoWebTorrentSeeder(store);
        queueMicrotask(() => {
            try {
                store.dispatchEvent(new CustomEvent('graph-update'));
            } catch {
                /* ignore */
            }
        });
    } catch (e) {
        if (epoch !== store._curriculumMountEpoch) return;
        if (!isRemoteSwrStillActive(store, source, sourceId)) return;
        const ui = store.ui || {};
        if (isUniverseRevokedError(e)) {
            queueMicrotask(() => {
                void promptStudentUniverseRevoked(store, {
                    source,
                    treeJson: store.state.rawGraphData,
                    keepViewingCached: true,
                });
            });
            console.warn('[Arborito] remote tree SWR refresh: universe revoked', e);
            return;
        }
        /* Offline / relay error: if a prior revoke notice was never dismissed, show it. */
        if (hasPendingUniverseRevokePrompt(source)) {
            queueMicrotask(() => {
                if (!hasPendingUniverseRevokePrompt(source)) return;
                void promptStudentUniverseRevoked(store, {
                    source,
                    treeJson: store.state.rawGraphData,
                    keepViewingCached: true,
                });
            });
            console.warn('[Arborito] remote tree SWR refresh (pending revoke)', e);
            return;
        }
        queueMicrotask(() =>
            store.notify(
                ui.treeLoadedFromCacheOffline ||
                    'Showing cached copy, network refresh failed.',
                false
            )
        );
        console.warn('[Arborito] remote tree SWR refresh', e);
    }
}
